/**
 * Pure view model of the venue card's editable parts (task T.F2): the link form
 * (create / edit), the manager picker's options and label, and the copy for the
 * outcomes a curator can act on.
 *
 * Framework-free AND bundler-free, like `./venues-model` — the `unit` Playwright
 * project imports it in plain Node, so nothing here may touch `import.meta.env`
 * (`./client`) or React. The api shapes arrive as TYPE-only imports (erased at
 * compile time) and the combobox option type comes from the framework-free
 * `combobox-model`.
 *
 * The link registry's two unique keys shape everything here:
 *   * `url` is the PRIMARY match key — the api's POST matches it first;
 *   * `(courseId, placeId)` is the secondary one.
 * So a form that re-uses a value ANOTHER row already holds is a 409 the server
 * will answer — `linkConflict` predicts it client-side, purely as a hint, and
 * the api stays the authority.
 */

import type { ComboboxOption } from '../../components/combobox-model';
import type { VenueCourseFormat } from '../../api/venues';
import type { UserDirectoryEntry, VenueLinkCreate, VenueLinkPatch } from '../../api/venue-links';

// ---------------------------------------------------------------------------
// The link form
// ---------------------------------------------------------------------------

/** What the add/edit form holds — every field as TEXT, until parsing says otherwise. */
export interface LinkFormModel {
  courseId: string;
  placeId: string;
  url: string;
  /** `''` = no format (the seeded rows the sheet never labelled). */
  format: VenueCourseFormat | '';
}

export const EMPTY_LINK_FORM: LinkFormModel = { courseId: '', placeId: '', url: '', format: '' };

export function linkFormFrom(row: { courseId: number; placeId: number; url: string; format?: VenueCourseFormat | null }): LinkFormModel {
  return {
    courseId: String(row.courseId),
    placeId: String(row.placeId),
    url: row.url,
    format: row.format ?? '',
  };
}

/** A positive integer, or null — `0`, `1.5`, `12abc` and junk are all junk. */
function positiveInt(text: string): number | null {
  return /^\d+$/.test(text.trim()) && Number(text.trim()) > 0 ? Number(text.trim()) : null;
}

/** `http(s)://…`, trimmed — `javascript:` and relative strings are not links. */
export function parseLinkUrl(raw: string): string | null {
  const value = raw.trim();
  if (value === '') return null;
  try {
    const parsed = new URL(value);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:' ? value : null;
  } catch {
    return null;
  }
}

export interface ParsedLinkForm {
  courseId: number;
  placeId: number;
  url: string;
  format: VenueCourseFormat | null;
}

/** The whole form parses, or null — the submit button's disabled state mirrors it. */
export function parseLinkForm(form: LinkFormModel): ParsedLinkForm | null {
  const courseId = positiveInt(form.courseId);
  const placeId = positiveInt(form.placeId);
  const url = parseLinkUrl(form.url);
  if (courseId === null || placeId === null || url === null) return null;
  return { courseId, placeId, url, format: form.format === '' ? null : form.format };
}

/** Per-field copy for the one mistake the field itself can name. */
export function linkFieldError(form: LinkFormModel, field: keyof LinkFormModel): string | undefined {
  switch (field) {
    case 'courseId':
      return form.courseId.trim() === '' || positiveInt(form.courseId) !== null ? undefined : 'Курс — положительное целое число';
    case 'placeId':
      return form.placeId.trim() === '' || positiveInt(form.placeId) !== null ? undefined : 'Позиция — положительное целое число';
    case 'url':
      return form.url.trim() === '' || parseLinkUrl(form.url) !== null ? undefined : 'Нужна абсолютная ссылка http(s)://…';
    default:
      return undefined;
  }
}

/** POST body, or null when the form does not parse. */
export function linkCreateFrom(form: LinkFormModel): VenueLinkCreate | null {
  return parseLinkForm(form);
}

/**
 * PATCH body of one row's edit: ONLY the keys that actually differ (`null` for
 * a cleared format), or null when nothing changed — a no-op must not even reach
 * the api, the same rule the server applies to #8.
 */
export function linkPatchFrom(
  current: { courseId: number; placeId: number; url: string; format?: VenueCourseFormat | null },
  form: LinkFormModel,
): VenueLinkPatch | null {
  const parsed = parseLinkForm(form);
  if (!parsed) return null;

  const patch: VenueLinkPatch = {};
  if (parsed.courseId !== current.courseId) patch.courseId = parsed.courseId;
  if (parsed.placeId !== current.placeId) patch.placeId = parsed.placeId;
  if (parsed.url !== current.url) patch.url = parsed.url;
  if ((parsed.format ?? null) !== (current.format ?? null)) patch.format = parsed.format;
  return Object.keys(patch).length > 0 ? patch : null;
}

/**
 * Did the user change anything against the form as it opened? The gate that
 * keeps a no-op submit from reaching the api (which would answer 200 and write
 * nothing — but also would close the form pretending something happened).
 */
export function linkFormIsDirty(initial: LinkFormModel, form: LinkFormModel): boolean {
  return (
    initial.courseId.trim() !== form.courseId.trim() ||
    initial.placeId.trim() !== form.placeId.trim() ||
    initial.url.trim() !== form.url.trim() ||
    initial.format !== form.format
  );
}

/**
 * The 409 the api will answer, predicted: another row of THIS venue already
 * holds the form's url, or its (courseId, placeId) slot. `selfId` is the row
 * being edited (its own values are not a conflict).
 */
export function linkConflict(
  form: LinkFormModel,
  rows: ReadonlyArray<{ id: number; courseId: number; placeId: number; url: string }>,
  selfId?: number,
): { field: 'url' | 'slot'; linkId: number } | null {
  const parsed = parseLinkForm(form);
  if (!parsed) return null;

  const byUrl = rows.find((row) => row.url === parsed.url && row.id !== selfId);
  if (byUrl) return { field: 'url', linkId: byUrl.id };
  const bySlot = rows.find(
    (row) => row.courseId === parsed.courseId && row.placeId === parsed.placeId && row.id !== selfId,
  );
  return bySlot ? { field: 'slot', linkId: bySlot.id } : null;
}

// ---------------------------------------------------------------------------
// The manager picker (#25.5)
// ---------------------------------------------------------------------------

/** The directory as combobox options — name first, e-mail as the searchable hint. */
export function managerComboboxOptions(directory: ReadonlyArray<UserDirectoryEntry>): ComboboxOption[] {
  return directory.map((entry) => ({
    value: String(entry.id),
    label: entry.displayName ?? entry.email,
    hint: entry.displayName ? entry.email : undefined,
  }));
}

/**
 * The header's manager cell: the display name the directory resolves, else the
 * bare id the #7 payload carries, else the absence marker. A name the directory
 * does not know is never invented.
 */
export function managerDisplayLabel(
  managerUserId: number | null | undefined,
  directory: ReadonlyArray<UserDirectoryEntry>,
): string {
  if (typeof managerUserId !== 'number' || !Number.isFinite(managerUserId) || managerUserId <= 0) return '—';
  const entry = directory.find((candidate) => candidate.id === managerUserId);
  return entry?.displayName ?? entry?.email ?? `№ ${managerUserId}`;
}

// ---------------------------------------------------------------------------
// Failure copy
// ---------------------------------------------------------------------------

/** The slice of `ApiError` the copy needs — structural, so plain Node can call this. */
export interface FailureShape {
  status?: number;
  code?: string;
  message?: string;
}

export const LINK_CONFLICT_TEXT =
  'Такая ссылка или пара «курс + позиция» уже есть в реестре — у каждой ссылки должен быть свой адрес.';

/** What a failed link write prints; the 409 is the one outcome the curator can act on. */
export function linkErrorText(error: FailureShape | null | undefined, fallback = 'Не удалось сохранить ссылку.'): string | null {
  if (!error) return null;
  if (error.code === 'CONFLICT' || error.status === 409) return LINK_CONFLICT_TEXT;
  if (error.code === 'FORBIDDEN' || error.status === 403) {
    return 'Недостаточно прав: реестром ссылок управляет администратор.';
  }
  const message = error.message?.trim();
  return message ? message : fallback;
}

/** The hint that tells a curator the change is on the security trail. */
export const AUDIT_HINT = 'Изменения журналируются';
